import { FC, useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { ROW_UNIQUE_KEY_NAME } from '../../../common/utils';
import { useAdditionalFormContext } from '../../../common/form';
import { encodeAlgorithmMappingOptionValue } from '../mappings/utils';
import { encodeAlgorithmSourceDimensionOptionValue } from '../sources/utils';
import {
  AlgorithmJoinItemSelectField,
  AlgorithmJoinItemSelectFieldProps,
} from './AlgorithmJoinItemSelectField';
import { AlgorithmJoinsFormStore } from './AlgorithmJoinsFormStore';
import {
  AlgorithmJoinItemValidFormDataType,
  AlgorithmJoinsValidFormDataType,
} from './schema/types';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './AlgorithmJoinItemSourceDimensionOrMappingSelectField.translations.json';
import { isDimensionWithCategories } from './utils';

const { addTranslations, useTranslation } = i18nNsFactory(
  'algorithm-join-item-source-dimension-select-field',
);
addTranslations(translations);

export interface AlgorithmJoinItemSourceDimensionOrMappingSelectFieldProps
  extends Omit<
    AlgorithmJoinItemSelectFieldProps,
    'name' | 'label' | 'value' | 'onSelect' | 'onClear'
  > {
  joinIndex: number;
  index: number;
  onChange?: (value: string | number, option?: any) => void;
}

export const AlgorithmJoinItemSourceDimensionOrMappingSelectField: FC<
  AlgorithmJoinItemSourceDimensionOrMappingSelectFieldProps
> = (props) => {
  const { joinIndex, index, onChange, ...rest } = props;
  const { t } = useTranslation();

  const { getValues, setValue } =
    useFormContext<AlgorithmJoinsValidFormDataType>();
  const { onChange: formOnChange } = useAdditionalFormContext();

  const name = `joins.${joinIndex}.algorithmJoinItems.${index}` as const;
  const value = useWatch<AlgorithmJoinsValidFormDataType, typeof name>({
    name,
  });
  const [algorithmSourcesByIds] = AlgorithmJoinsFormStore.useValue(
    'algorithmSourcesByIds',
  );

  const selectedValue = useMemo(() => {
    if (!value) {
      return '';
    }

    if ('algorithmSourceId' in value && value.sourceDimension) {
      const algorithmSource = algorithmSourcesByIds.get(
        value.algorithmSourceId,
      )!;

      const dimension = algorithmSource.dimensions.find(
        (d) => d.code === value.sourceDimension,
      )!;
      return encodeAlgorithmSourceDimensionOptionValue(
        algorithmSource,
        dimension,
      );
    }

    if (
      value._variant?.startsWith('MAPPING_') &&
      'algorithmMappingAlias' in value &&
      value.algorithmMappingAlias
    ) {
      return encodeAlgorithmMappingOptionValue(
        value.algorithmMappingAlias as string,
      );
    }

    return '';
  }, [value, algorithmSourcesByIds]);

  const handleSelect: AlgorithmJoinItemSelectFieldProps['onSelect'] =
    useCallback(
      (value, option) => {
        const current = getValues(name);

        if (!current) {
          return;
        }

        const base = {
          [ROW_UNIQUE_KEY_NAME]: current[ROW_UNIQUE_KEY_NAME],
          id: current.id,
        };

        let next: AlgorithmJoinItemValidFormDataType | undefined = undefined;

        if ('dimension' in option) {
          const newAlgorithmSourceId = option.algorithmSource.id;
          const newDimensionCode = option.dimension.code;
          const withCategories = isDimensionWithCategories(option.dimension);

          if (
            current._variant === 'DIMENSION_DIMENSION' ||
            current._variant === 'MAPPING_DIMENSION'
          ) {
            next = {
              ...base,
              _variant: 'DIMENSION_DIMENSION',
              algorithmSourceId: newAlgorithmSourceId,
              sourceDimension: newDimensionCode,
              targetDimension: current.targetDimension,
            };
          } else if (current._variant === 'DIMENSION_VALUE') {
            next = {
              ...base,
              _variant: 'DIMENSION_VALUE',
              algorithmSourceId: newAlgorithmSourceId,
              sourceDimension: newDimensionCode,
              targetValue: withCategories ? '' : current.targetValue,
              targetValueFormat: current.targetValueFormat,
              targetValueDataType: current.targetValueDataType,
            };
          } else if (current._variant === 'MAPPING_VALUE') {
            next = {
              ...base,
              _variant: 'DIMENSION_VALUE',
              algorithmSourceId: newAlgorithmSourceId,
              sourceDimension: newDimensionCode,
              targetValue: '',
            };
          } else if (current._variant === 'DIMENSION_MAPPING') {
            next = {
              ...base,
              _variant: 'DIMENSION_MAPPING',
              algorithmSourceId: newAlgorithmSourceId,
              sourceDimension: newDimensionCode,
              algorithmMappingAlias: current.algorithmMappingAlias,
            };
          }
        } else if ('mapping' in option) {
          if (
            current._variant === 'DIMENSION_DIMENSION' ||
            current._variant === 'MAPPING_DIMENSION'
          ) {
            next = {
              ...base,
              _variant: 'MAPPING_DIMENSION',
              algorithmMappingAlias: option.mapping.alias,
              targetDimension: current.targetDimension,
            };
          } else if (
            current._variant === 'DIMENSION_VALUE' ||
            current._variant === 'MAPPING_VALUE'
          ) {
            next = {
              ...base,
              _variant: 'MAPPING_VALUE',
              algorithmMappingAlias: option.mapping.alias,
              targetValue: '',
            };
          }
        }

        if (!next) {
          throw new Error(
            `Unexpected variant "${current._variant}" or option shape in source dimension/mapping select:\n${JSON.stringify(option, null, 2)}`,
          );
        }

        setValue(name, next, { shouldDirty: true });
        onChange?.(value, option);
        formOnChange?.();
      },
      [name, onChange, getValues, setValue, formOnChange],
    );

  const handleClear: AlgorithmJoinItemSelectFieldProps['onClear'] =
    useCallback(() => {
      const current = getValues(name);

      if (!current) {
        return;
      }

      const base = {
        [ROW_UNIQUE_KEY_NAME]: current[ROW_UNIQUE_KEY_NAME],
        id: current.id,
      };

      let next: AlgorithmJoinItemValidFormDataType | undefined = undefined;

      if (
        current._variant === 'DIMENSION_DIMENSION' ||
        current._variant === 'MAPPING_DIMENSION'
      ) {
        next = {
          ...base,
          _variant: 'DIMENSION_DIMENSION',
          algorithmSourceId: null!,
          sourceDimension: '',
          targetDimension: current.targetDimension,
        };
      } else if (current._variant === 'DIMENSION_VALUE') {
        next = {
          ...base,
          _variant: 'DIMENSION_VALUE',
          algorithmSourceId: null!,
          sourceDimension: '',
          targetValue: current.targetValue,
          targetValueFormat: current.targetValueFormat,
          targetValueDataType: current.targetValueDataType,
        };
      } else if (current._variant === 'MAPPING_VALUE') {
        next = {
          ...base,
          _variant: 'DIMENSION_VALUE',
          algorithmSourceId: null!,
          sourceDimension: '',
          targetValue: '',
        };
      } else if (current._variant === 'DIMENSION_MAPPING') {
        next = {
          ...base,
          _variant: 'DIMENSION_MAPPING',
          algorithmSourceId: null!,
          sourceDimension: '',
          algorithmMappingAlias: current.algorithmMappingAlias,
        };
      }

      if (!next) {
        throw new Error(
          `Unexpected variant "${current._variant}" in source dimension/mapping clear`,
        );
      }

      setValue(name, next, { shouldDirty: true });
      onChange?.(undefined!, undefined!);
      formOnChange?.();
    }, [name, onChange, getValues, setValue, formOnChange]);

  const fieldName = value?._variant?.startsWith('MAPPING_')
    ? 'algorithmMappingAlias'
    : 'sourceDimension';

  return (
    <AlgorithmJoinItemSelectField
      name={`${name}.${fieldName}`}
      label={t('source-dimension')}
      value={selectedValue}
      onSelect={handleSelect}
      onClear={handleClear}
      {...rest}
    />
  );
};
